import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert, CircularProgress,
  List, ListItem, ListItemText, Divider
} from '@mui/material';
import { settlementsApi } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import SettleUpDialog from './SettleUpDialog';
import { formatMoney } from '../../utils/money';

const formatAmount = (value) => formatMoney(Number(value));
const personName = (person) => (person.isYou ? 'You' : person.name || person.email);

function BalancesSection() {
  const [balances, setBalances] = useState([]);
  const [summary, setSummary] = useState({ youOwe: 0, youAreOwed: 0, netBalance: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settleTarget, setSettleTarget] = useState(null);
  const { showError } = useNotification();

  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      setLoading(true);
      const [balancesRes, historyRes] = await Promise.all([
        settlementsApi.getBalances(),
        settlementsApi.getHistory()
      ]);
      setBalances(balancesRes.data.balances || []);
      setSummary(balancesRes.data.summary || { youOwe: 0, youAreOwed: 0, netBalance: 0 });
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Error loading balances:', error);
      showError('Failed to load balances');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">You Owe</Typography>
              <Typography variant="h4">{formatAmount(summary.youOwe)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">You Are Owed</Typography>
              <Typography variant="h4">{formatAmount(summary.youAreOwed)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6">Net Balance</Typography>
              <Typography
                variant="h4"
                color={summary.netBalance >= 0 ? 'success.main' : 'error.main'}
              >
                {summary.netBalance < 0 ? '-' : ''}{formatAmount(Math.abs(summary.netBalance))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Balances Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>Balance Breakdown</Typography>

          {balances.length === 0 ? (
            <Alert severity="info">No balances to show. All settled up! 🎉</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Friend</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right">Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balances.map((balance) => {
                    const owes = balance.balance < 0;
                    const amount = Math.abs(balance.balance);

                    return (
                      <TableRow key={balance.email}>
                        <TableCell>
                          {balance.name || '—'}
                          {!balance.isOnPlatform && (
                            <Chip label="Not on platform" size="small" variant="outlined" sx={{ ml: 1 }} />
                          )}
                          {balance.breakdown?.length > 1 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {balance.breakdown
                                .map(b => `${b.groupName || 'Non-group'}: ${b.balance < 0 ? '-' : '+'}${formatAmount(Math.abs(b.balance))}`)
                                .join(' · ')}
                            </Typography>
                          )}
                          {balance.breakdown?.length === 1 && balance.breakdown[0].groupName && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              In {balance.breakdown[0].groupName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{balance.email}</TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight="bold"
                            color={owes ? 'error.main' : 'success.main'}
                          >
                            {owes ? '-' : '+'}{formatAmount(amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={owes ? 'You owe' : 'Owes you'}
                            color={owes ? 'error' : 'success'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setSettleTarget(balance)}
                          >
                            Settle Up
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Settlement History */}
      <Card>
        <CardContent>
          <Typography variant="h6" mb={1}>Settlement History</Typography>
          {history.length === 0 ? (
            <Alert severity="info">No settlements recorded yet.</Alert>
          ) : (
            <List disablePadding>
              {history.map((entry, index) => (
                <React.Fragment key={entry.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`${personName(entry.from)} paid ${personName(entry.to)}`}
                      secondary={`${new Date(entry.settledAt).toLocaleDateString()}${entry.group ? ` · ${entry.group.name}` : ''}`}
                    />
                    <Typography
                      fontWeight="bold"
                      color={entry.direction === 'received' ? 'success.main' : 'text.primary'}
                    >
                      {formatAmount(entry.amount)}
                    </Typography>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <SettleUpDialog
        open={!!settleTarget}
        target={settleTarget}
        onClose={() => setSettleTarget(null)}
        onSettled={loadBalances}
      />
    </Box>
  );
}

export default BalancesSection;
